import { COLOR } from 'app-v2/design-tokens'
import { Geolocation, Search } from 'app/assets/icons'
import { push } from 'connected-react-router'
import ExecutionEnvironment from 'exenv'
import qs from 'qs'
import React, { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { colors as oldColors } from 'shared/assets/styles'
import { eventMetadata, eventsDefinitions } from 'shared/store/metadata'
import type { RootState } from 'shared/store/reducers'
import type { Coordinates, SearchParams } from 'shared/store/store-locator'
import {
    search,
    setUserPosition as setUserPositionDispatch
} from 'shared/store/store-locator'
import { getCurrentPosition } from 'shared/utils'
import {
    GlobalStyle,
    Input,
    InputContainer,
    InputIcon,
    StyledInput
} from './styles'
import type { InputAddressAutocompleteProps } from './type'

const { yellow, offWhite } = oldColors

const defaultFilters: any = {
    components: {
        country: [process.env.REACT_APP_WOOSMAP_COUNTRY]
    },
    types: [
        'locality',
        'postal_code',
        'address',
        'shopping',
        'tourist_attraction',
        'country',
        'airport',
        'train_station',
        'metro_station',
        'museum',
        'art_gallery'
    ],
    typesDelivery: ['address'],
    data: 'advanced',
    customDescription:
        'postal_code:"{name} ({postal_town}) - {administrative_area_level_0}"'
}

export const InputAddressAutocomplete = ({
    placeholder = 'storelocator.search.button',
    defaultSearch,
    radiusDistance,
    filters = defaultFilters,
    delivery = false,
    iconColor = yellow,
    backgroundColor = offWhite,
    radius = 20,
    textSize = 14,
    height = 2,
    shadow,
    forKingsBirthday,
    forQuickSearch,
    forTPK = false,
    hasBorder = false,
    onSearch,
    handleOnQuickSearchChange,
    handleOnSuggestionChanged
}: InputAddressAutocompleteProps) => {
    const [searchValue, setSearchValue] = useState(defaultSearch || '')
    const [suggestion, setSuggestion] = useState(undefined)
    const intl = useIntl()
    const dispatch = useDispatch()
    const selectedServices = useSelector(
        (state: RootState) => state.storeLocatorPageState.selectedServices
    )
    const ref = useRef()

    const navigate = (to: string) => dispatch(push(to))

    useEffect(() => {
        let localitiesWidget = new window.woosmap.localities.Autocomplete(
            'my-input-localities',
            { ...filters, debounceTime: 300 }
        )
        localitiesWidget.addListener('selected_suggestion', () => {
            const selection = localitiesWidget.getSelectedSuggestionDetails()
            onSelected(selection)
        })

        return () => localitiesWidget.removeListener('selected_suggestion')
    }, [])

    useEffect(() => {
        const cleanButton = document.querySelector('.localities-empty-button')
        if (searchValue.length > 0 && cleanButton) {
            cleanButton.addEventListener('click', () => {
                setSearchValue('')
            })
        }
    }, [searchValue.length])

    useEffect(() => {
        handleOnQuickSearchChange && handleOnQuickSearchChange(searchValue)
        !searchValue &&
            handleOnSuggestionChanged &&
            handleOnSuggestionChanged(undefined)
    }, [searchValue])

    useEffect(() => {
        handleOnSuggestionChanged && handleOnSuggestionChanged(suggestion)
    }, [suggestion])

    const onSelected = infos => {
        const address = infos.formatted_address
        const coords = infos.geometry.location
        const meta = {
            ...eventsDefinitions.RESTAURANT_SEARCH,
            event_label: address
        }
        const storeLocatorRoute = intl.formatMessage({
            id: 'routes.restaurants'
        })

        setSuggestion(infos)

        dispatch(eventMetadata(meta))
        dispatch(
            search(
                {
                    address,
                    coords,
                    filters,
                    radius: radiusDistance,
                    limit: delivery ? 1 : 50
                },
                'search'
            )
        ).then(() => {
            if (forKingsBirthday) {
                const queryString = qs.stringify({
                    place: address
                })
                navigate(`${storeLocatorRoute}?${queryString}`)
            }
        })

        onSearch && onSearch(address)
        setSearchValue(address)
    }

    const onGeolocation = () => {
        const searchNearUserPosition = (p: SearchParams) =>
            dispatch(search(p, 'nearest'))
        const setUserPosition = (p: Coordinates) =>
            dispatch(setUserPositionDispatch(p))

        ExecutionEnvironment.canUseDOM &&
            getCurrentPosition({
                searchNearUserPosition,
                setUserPosition,
                filters: selectedServices,
                geolocation: navigator.geolocation
            })
    }

    return (
        <Input
            backgroundColor={delivery ? COLOR.SECONDARY : backgroundColor}
            radius={radius}
            height={height}
            shadow={shadow}
            forQuickSearch={forQuickSearch}
            ref={ref}
            hasBorder={hasBorder}>
            <GlobalStyle
                backgroundColor={delivery ? COLOR.SECONDARY : backgroundColor}
                textSize={textSize}
                size={ref?.current?.offsetWidth}
                forQuickSearch={forQuickSearch}
            />
            <InputIcon>
                <Search size={25} color={COLOR.PRIMARY} />
            </InputIcon>
            <InputContainer>
                <StyledInput
                    id="my-input-localities"
                    value={searchValue || ''}
                    onChange={e => setSearchValue(e.target.value)}
                    placeholder={intl.formatMessage({
                        id: placeholder
                    })}
                    backgroundColor={
                        delivery ? COLOR.SECONDARY : backgroundColor
                    }
                    textSize={textSize}
                    forQuickSearch={forQuickSearch}
                    forTPK={forTPK}
                />
            </InputContainer>
            {!!!searchValue && !forQuickSearch && (
                <InputIcon onClick={onGeolocation} clickable>
                    <Geolocation size={25} color={iconColor} />
                </InputIcon>
            )}
        </Input>
    )
}
